import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { cn, formatDate, timeAgo, formatDuration, getStatusColor } from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card, CardHeader, CardTitle, CardBody, StatCard } from '@/components/ui/Card';
import { Badge, StatusDot } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { Drawer } from '@/components/ui/Drawer';
import { toast } from '@/components/ui/Toast';
import type { RemoteSession, Device } from '@/types';
import {
  Monitor,
  Search,
  RefreshCw,
  Plus,
  Filter,
  Eye,
  Unlock,
  Clock,
  User,
  Power,
  FileText,
  ExternalLink,
  Shield,
  CheckCircle,
  AlertTriangle,
  Terminal,
  FolderOpen,
  Calendar,
  X,
  ChevronRight,
  MoreVertical,
} from 'lucide-react';

export function RemoteSessions() {
  const navigate = useNavigate();

  // Data state
  const [sessions, setSessions] = useState<RemoteSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState<Device[]>([]);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [modeFilter, setModeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Selected session for drawer / details
  const [selectedSession, setSelectedSession] = useState<RemoteSession | null>(null);
  const [drawerNotes, setDrawerNotes] = useState<string>('');

  // End session modal state
  const [sessionToEnd, setSessionToEnd] = useState<RemoteSession | null>(null);
  const [showEndModal, setShowEndModal] = useState(false);

  // New session modal state
  const [showNewSessionModal, setShowNewSessionModal] = useState(false);
  const [selectedTargetDevice, setSelectedTargetDevice] = useState<string>('');
  const [newSessionMode, setNewSessionMode] = useState<'control' | 'view_only'>('control');

  // Load session data
  const fetchSessions = async () => {
    setLoading(true);
    try {
      const data = await mockApi.getRemoteSessions();
      setSessions(data);

      const devRes = await mockApi.getDevices({ pageSize: 100 });
      setDevices(devRes.data);
    } catch (err) {
      toast('Failed to load remote sessions', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  // Sync drawer notes when selected session changes
  useEffect(() => {
    if (selectedSession) {
      setDrawerNotes(selectedSession.notes || '');
    }
  }, [selectedSession]);

  // Filtered sessions
  const filteredSessions = useMemo(() => {
    return sessions.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (modeFilter !== 'all' && s.mode !== modeFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          s.hostname.toLowerCase().includes(q) ||
          s.technicianName.toLowerCase().includes(q) ||
          s.ipAddress.includes(q) ||
          s.id.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [sessions, statusFilter, modeFilter, searchQuery]);

  // Stat metrics calculations
  const stats = useMemo(() => {
    const activeCount = sessions.filter((s) => s.status === 'active').length;
    const totalCount = sessions.length;

    const durations = sessions.map((s) => s.duration).filter((d) => d > 0);
    const avgDuration = durations.length > 0
      ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length)
      : 0;

    const uniqueTechs = new Set(sessions.map((s) => s.technicianName)).size;

    return { activeCount, totalCount, avgDuration, uniqueTechs };
  }, [sessions]);

  // Action: End session confirmation
  const handleConfirmEndSession = () => {
    if (!sessionToEnd) return;

    setSessions((prev) =>
      prev.map((s) =>
        s.id === sessionToEnd.id
          ? { ...s, status: 'ended', endedAt: new Date().toISOString() }
          : s
      )
    );

    if (selectedSession?.id === sessionToEnd.id) {
      setSelectedSession((prev) =>
        prev ? { ...prev, status: 'ended', endedAt: new Date().toISOString() } : null
      );
    }

    toast(`Session on ${sessionToEnd.hostname} terminated successfully`, 'success');
    setShowEndModal(false);
    setSessionToEnd(null);
  };

  // Action: Save notes in drawer
  const handleSaveNotes = () => {
    if (!selectedSession) return;
    setSessions((prev) =>
      prev.map((s) => (s.id === selectedSession.id ? { ...s, notes: drawerNotes } : s))
    );
    setSelectedSession((prev) => (prev ? { ...prev, notes: drawerNotes } : null));
    toast('Session notes updated successfully', 'success');
  };

  // Action: Launch new session
  const handleLaunchSession = () => {
    if (!selectedTargetDevice) {
      toast('Please select a target device', 'warning');
      return;
    }
    setShowNewSessionModal(false);
    navigate(`/devices/${selectedTargetDevice}/remote-desktop`);
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-text-primary tracking-tight">Remote Sessions</h1>
            <Badge variant="online" dot>
              {stats.activeCount} Active
            </Badge>
          </div>
          <p className="text-xs text-text-muted mt-1">
            Monitor, manage, and audit active and historical remote desktop & terminal connections across all endpoints.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={fetchSessions} disabled={loading}>
            <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', loading && 'animate-spin')} /> Refresh
          </Button>
          <Button variant="primary" size="sm" onClick={() => setShowNewSessionModal(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Start New Session
          </Button>
        </div>
      </div>

      {/* Overview Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Active Sessions"
          value={stats.activeCount}
          status="online"
          sublabel="Currently connected endpoints"
        />
        <StatCard
          label="Total Sessions Today"
          value={stats.totalCount}
          status="info"
          sublabel="All active & completed today"
        />
        <StatCard
          label="Average Duration"
          value={formatDuration(stats.avgDuration)}
          sublabel="Mean technician remote time"
        />
        <StatCard
          label="Unique Technicians"
          value={stats.uniqueTechs}
          sublabel="Active admin operators"
        />
      </div>

      {/* Search & Filter Bar */}
      <Card className="p-4 surface border-border">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-[280px] max-w-md">
            <div className="relative w-full">
              <Search className="h-3.5 w-3.5 text-text-muted absolute left-3 top-2.5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search hostname, technician, or IP address..."
                className="input-base pl-9 text-xs"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Status Filter */}
            <div className="flex items-center gap-1.5 text-xs text-text-muted">
              <Filter className="h-3.5 w-3.5" />
              <span>Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-bg border border-border rounded px-2.5 py-1 text-xs text-text-primary focus:outline-none focus:border-accent font-mono cursor-pointer"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="disconnected">Disconnected</option>
                <option value="expired">Expired</option>
                <option value="ended">Ended</option>
              </select>
            </div>

            {/* Mode Filter */}
            <div className="flex items-center gap-1.5 text-xs text-text-muted">
              <span>Mode:</span>
              <select
                value={modeFilter}
                onChange={(e) => setModeFilter(e.target.value)}
                className="bg-bg border border-border rounded px-2.5 py-1 text-xs text-text-primary focus:outline-none focus:border-accent font-mono cursor-pointer"
              >
                <option value="all">All Modes</option>
                <option value="control">Control</option>
                <option value="view_only">View Only</option>
              </select>
            </div>

            {(statusFilter !== 'all' || modeFilter !== 'all' || searchQuery) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStatusFilter('all');
                  setModeFilter('all');
                  setSearchQuery('');
                }}
                className="text-2xs text-text-muted"
              >
                Reset Filters
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Main Remote Sessions Table */}
      <Card className="surface border-border overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12 text-text-muted gap-3">
            <RefreshCw className="h-6 w-6 animate-spin text-accent" />
            <span className="text-xs font-mono">Loading remote sessions log...</span>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-text-muted gap-3 text-center">
            <Monitor className="h-10 w-10 text-border-strong" />
            <span className="text-sm font-medium">No remote sessions found</span>
            <span className="text-xs">No sessions match your active filters or search criteria.</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setStatusFilter('all');
                setModeFilter('all');
                setSearchQuery('');
              }}
              className="mt-2"
            >
              Reset Filters
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-panel border-b border-border text-text-muted font-mono select-none">
                <tr>
                  <th className="p-3.5">Device Hostname</th>
                  <th className="p-3.5">Technician</th>
                  <th className="p-3.5">Mode</th>
                  <th className="p-3.5">Status</th>
                  <th className="p-3.5">Started At</th>
                  <th className="p-3.5">Duration</th>
                  <th className="p-3.5">IP Address</th>
                  <th className="p-3.5">Consent</th>
                  <th className="p-3.5">Notes</th>
                  <th className="p-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredSessions.map((session) => {
                  const isActive = session.status === 'active';
                  return (
                    <tr
                      key={session.id}
                      onClick={() => setSelectedSession(session)}
                      className={cn(
                        'hover:bg-hover transition-colors cursor-pointer group',
                        selectedSession?.id === session.id && 'bg-accent/10'
                      )}
                    >
                      {/* Hostname */}
                      <td className="p-3.5 font-medium">
                        <div className="flex items-center gap-2">
                          <Monitor className="h-4 w-4 text-accent shrink-0" />
                          <Link
                            to={`/devices/${session.deviceId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-text-primary hover:text-accent hover:underline font-semibold"
                          >
                            {session.hostname}
                          </Link>
                        </div>
                      </td>

                      {/* Technician */}
                      <td className="p-3.5">
                        <div className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5 text-text-muted" />
                          <span className="text-text-secondary">{session.technicianName}</span>
                        </div>
                      </td>

                      {/* Mode Badge */}
                      <td className="p-3.5">
                        <Badge variant={session.mode === 'control' ? 'online' : 'warning'} className="uppercase">
                          {session.mode === 'control' ? (
                            <span className="flex items-center gap-1">
                              <Unlock className="h-3 w-3" /> Control
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <Eye className="h-3 w-3" /> View
                            </span>
                          )}
                        </Badge>
                      </td>

                      {/* Status Badge */}
                      <td className="p-3.5">
                        <div className="flex items-center gap-2">
                          <StatusDot status={session.status} />
                          <span className="capitalize font-mono text-text-secondary">{session.status}</span>
                        </div>
                      </td>

                      {/* Started At */}
                      <td className="p-3.5 font-mono text-text-muted" title={formatDate(session.startedAt)}>
                        {timeAgo(session.startedAt)}
                      </td>

                      {/* Duration */}
                      <td className="p-3.5 font-mono text-text-muted">
                        {formatDuration(session.duration)}
                      </td>

                      {/* IP Address */}
                      <td className="p-3.5 font-mono text-text-muted">{session.ipAddress}</td>

                      {/* Consent */}
                      <td className="p-3.5">
                        {session.consentGiven ? (
                          <span className="text-emerald-400 flex items-center gap-1 text-2xs font-mono">
                            <CheckCircle className="h-3 w-3" /> Granted
                          </span>
                        ) : (
                          <span className="text-amber-400 flex items-center gap-1 text-2xs font-mono">
                            <AlertTriangle className="h-3 w-3" /> Bypassed
                          </span>
                        )}
                      </td>

                      {/* Notes preview */}
                      <td className="p-3.5 max-w-xs truncate text-text-muted italic">
                        {session.notes || 'No notes added'}
                      </td>

                      {/* Action Menu */}
                      <td className="p-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {isActive ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/devices/${session.deviceId}/remote-desktop`)}
                                title="Re-attach Remote Session"
                              >
                                Attach
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => {
                                  setSessionToEnd(session);
                                  setShowEndModal(true);
                                }}
                                title="End active session"
                              >
                                End
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedSession(session)}
                            >
                              Details
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Expandable Session Detail Drawer */}
      <Drawer
        open={!!selectedSession}
        onClose={() => setSelectedSession(null)}
        title={selectedSession ? `Session Details: ${selectedSession.hostname}` : ''}
        width={640}
      >
        {selectedSession && (
          <div className="space-y-6 text-xs">
            {/* Header info card */}
            <div className="p-4 bg-panel rounded-lg border border-border space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Monitor className="h-5 w-5 text-accent" />
                  <span className="font-semibold text-sm text-text-primary">{selectedSession.hostname}</span>
                </div>
                <Badge variant={selectedSession.status === 'active' ? 'online' : 'offline'} className="uppercase">
                  {selectedSession.status}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3 font-mono text-2xs text-text-secondary border-t border-border pt-3">
                <div>
                  <span className="text-text-muted block">Session ID</span>
                  <span className="text-text-primary font-semibold">{selectedSession.id}</span>
                </div>
                <div>
                  <span className="text-text-muted block">Technician</span>
                  <span className="text-text-primary font-semibold">{selectedSession.technicianName}</span>
                </div>
                <div>
                  <span className="text-text-muted block">Mode</span>
                  <span className="text-text-primary capitalize font-semibold">{selectedSession.mode}</span>
                </div>
                <div>
                  <span className="text-text-muted block">Client IP Address</span>
                  <span className="text-text-primary font-semibold">{selectedSession.ipAddress}</span>
                </div>
                <div>
                  <span className="text-text-muted block">Started At</span>
                  <span className="text-text-primary">{formatDate(selectedSession.startedAt)}</span>
                </div>
                <div>
                  <span className="text-text-muted block">Total Duration</span>
                  <span className="text-text-primary">{formatDuration(selectedSession.duration)}</span>
                </div>
              </div>
            </div>

            {/* Quick Link Tools */}
            <div className="space-y-2">
              <span className="text-2xs font-semibold text-text-muted uppercase tracking-wider block">
                Quick Navigation & Tools
              </span>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/devices/${selectedSession.deviceId}`)}
                  className="justify-start"
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5 text-accent" /> Device Overview
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/devices/${selectedSession.deviceId}/powershell`)}
                  className="justify-start"
                >
                  <Terminal className="h-3.5 w-3.5 mr-1.5 text-emerald-400" /> Open Terminal
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/devices/${selectedSession.deviceId}/files`)}
                  className="justify-start"
                >
                  <FolderOpen className="h-3.5 w-3.5 mr-1.5 text-amber-400" /> File Explorer
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate('/audit-log')}
                  className="justify-start"
                >
                  <Shield className="h-3.5 w-3.5 mr-1.5 text-blue-400" /> View Audit Trail
                </Button>
              </div>
            </div>

            {/* Editable Notes Section */}
            <div className="space-y-2">
              <label className="text-2xs font-semibold text-text-muted uppercase tracking-wider block">
                Session Audit Notes
              </label>
              <textarea
                value={drawerNotes}
                onChange={(e) => setDrawerNotes(e.target.value)}
                placeholder="Enter technician notes..."
                className="w-full h-28 bg-bg border border-border rounded p-2 text-xs text-text-primary focus:outline-none focus:border-accent"
              />
              <div className="flex justify-end">
                <Button variant="primary" size="sm" onClick={handleSaveNotes}>
                  Save Notes
                </Button>
              </div>
            </div>

            {/* Action Bar inside Drawer */}
            {selectedSession.status === 'active' && (
              <div className="border-t border-border pt-4 flex items-center justify-between">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(`/devices/${selectedSession.deviceId}/remote-desktop`)}
                >
                  <Monitor className="h-3.5 w-3.5 mr-1.5" /> Attach to Viewport
                </Button>

                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    setSessionToEnd(selectedSession);
                    setShowEndModal(true);
                  }}
                >
                  <Power className="h-3.5 w-3.5 mr-1.5" /> Terminate Session
                </Button>
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* End Session Confirmation Modal */}
      <ConfirmDialog
        open={showEndModal}
        onClose={() => setShowEndModal(false)}
        onConfirm={handleConfirmEndSession}
        title="Terminate Remote Session"
        confirmLabel="Terminate Session"
        danger
        message={
          <div className="space-y-2 text-xs">
            <p>
              Are you sure you want to forcibly terminate the active remote session on{' '}
              <strong>{sessionToEnd?.hostname}</strong>?
            </p>
            <p className="text-2xs text-text-muted">
              The technician and endpoint user will be immediately disconnected.
            </p>
          </div>
        }
      />

      {/* Launch New Session Modal */}
      <Modal
        open={showNewSessionModal}
        onClose={() => setShowNewSessionModal(false)}
        title="Start New Remote Desktop Session"
        size="lg"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowNewSessionModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleLaunchSession}>
              Connect Remote Session
            </Button>
          </>
        }
      >
        <div className="space-y-4 text-xs">
          <div>
            <label className="block text-text-muted font-medium mb-1">Select Target Device:</label>
            <select
              value={selectedTargetDevice}
              onChange={(e) => setSelectedTargetDevice(e.target.value)}
              className="input-base text-xs font-mono"
            >
              <option value="">-- Choose an online device --</option>
              {devices.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.hostname} [{d.client}] - {d.status.toUpperCase()} ({d.localIp})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-text-muted font-medium mb-1">Interaction Mode:</label>
            <div className="grid grid-cols-2 gap-3">
              <div
                onClick={() => setNewSessionMode('control')}
                className={cn(
                  'p-3 rounded border cursor-pointer flex flex-col gap-1',
                  newSessionMode === 'control' ? 'border-accent bg-accent/10' : 'border-border bg-panel'
                )}
              >
                <span className="font-semibold text-text-primary">Control Mode</span>
                <span className="text-2xs text-text-muted">Interactive keyboard & mouse control</span>
              </div>

              <div
                onClick={() => setNewSessionMode('view_only')}
                className={cn(
                  'p-3 rounded border cursor-pointer flex flex-col gap-1',
                  newSessionMode === 'view_only' ? 'border-accent bg-accent/10' : 'border-border bg-panel'
                )}
              >
                <span className="font-semibold text-text-primary">View Only Mode</span>
                <span className="text-2xs text-text-muted">Screen observation only</span>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
